import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FloatField } from "@/components/ui/FormField";
import {
  Settings2,
  Globe,
  Edit3,
  History,
  ChevronDown,
  ChevronUp,
  DollarSign,
} from "lucide-react";
import {
  apiGetSalaryConfig,
  apiGetCountryPresets,
  apiGetTaxRuleHistory,
  apiSetSalaryCountry,
  apiAddTaxRule,
  apiUpdateSalaryConfigParams,
  apiRecalculatePayrollRun,
  type SalaryTaxRule,
  type AffectedDraftRun,
} from "@/lib/api";
import { MONTH_NAMES_EN } from "./payroll-constants";

function TaxSettingsTab() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const translatedMonthNames = t("payroll.monthNames", { returnObjects: true });
  const monthNames = Array.isArray(translatedMonthNames) ? translatedMonthNames : MONTH_NAMES_EN;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["salary-config"],
    queryFn: apiGetSalaryConfig,
    staleTime: 30_000,
  });
  const { data: countries = [] } = useQuery({
    queryKey: ["salary-countries"],
    queryFn: apiGetCountryPresets,
    staleTime: Infinity,
  });
  const { data: history = [], refetch: refetchHistory } = useQuery({
    queryKey: ["salary-rule-history"],
    queryFn: apiGetTaxRuleHistory,
    enabled: false,
  });

  const [showHistory, setShowHistory] = useState(false);
  const [editRule, setEditRule] = useState<SalaryTaxRule | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editDate, setEditDate] = useState(new Date().toISOString().split("T")[0]);
  const [savingRule, setSavingRule] = useState(false);
  const [affectedRuns, setAffectedRuns] = useState<AffectedDraftRun[]>([]);
  const [showAffectedDialog, setShowAffectedDialog] = useState(false);
  const [changingCountry, setChangingCountry] = useState(false);
  const [editingParams, setEditingParams] = useState(false);
  const [paramForm, setParamForm] = useState({ averageSalary: "", maxBaseMultiplier: "", minNet: "", minGross: "" });

  const config = data?.config;
  const rules = data?.rules ?? [];

  const saveCountryMutation = useMutation({
    mutationFn: (code: string) => apiSetSalaryCountry(code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary-config"] });
      queryClient.invalidateQueries({ queryKey: ["salary-rule-history"] });
      queryClient.invalidateQueries({ queryKey: ["company-currency"] });
      queryClient.invalidateQueries({ queryKey: ["salary"] });
      toast({ title: t("payroll.taxSettings.toast.countryUpdated"), description: t("payroll.taxSettings.toast.countryUpdatedDesc") });
      setChangingCountry(false);
    },
    onError: () => toast({ title: t("payroll.taxSettings.toast.error"), description: t("payroll.taxSettings.toast.countryError"), variant: "destructive" }),
  });

  const saveParamsMutation = useMutation({
    mutationFn: (p: { averageSalary?: number; maxBaseMultiplier?: number; minNet?: number; minGross?: number }) =>
      apiUpdateSalaryConfigParams(p),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["salary-config"] });
      toast({ title: t("payroll.taxSettings.toast.paramsSaved") });
      setEditingParams(false);
    },
    onError: () => toast({ title: t("payroll.taxSettings.toast.error"), description: t("payroll.taxSettings.toast.paramsError"), variant: "destructive" }),
  });

  const recalcMutation = useMutation({
    mutationFn: (runId: number) => apiRecalculatePayrollRun(runId),
    onSuccess: (data) => toast({ title: t("payroll.taxSettings.toast.recalculated"), description: t("payroll.taxSettings.toast.recalculatedDesc", { count: data.recalculated }) }),
    onError: () => toast({ title: t("payroll.taxSettings.toast.error"), description: t("payroll.taxSettings.toast.recalcError"), variant: "destructive" }),
  });

  const handleOpenEdit = (rule: SalaryTaxRule) => {
    setEditRule(rule);
    setEditValue(rule.valueType === "rate" ? (rule.value * 100).toFixed(4).replace(/\.?0+$/, "") : String(rule.value));
    setEditDate(new Date().toISOString().split("T")[0]);
  };

  const handleSaveRule = async () => {
    if (!editRule || !editValue || !editDate) return;
    setSavingRule(true);
    try {
      const numericValue = editRule.valueType === "rate"
        ? parseFloat(editValue) / 100
        : parseFloat(editValue);
      if (isNaN(numericValue)) throw new Error(t("payroll.taxSettings.toast.invalidValue"));

      const result = await apiAddTaxRule({
        ruleKey: editRule.ruleKey,
        label: editRule.label,
        valueType: editRule.valueType,
        value: numericValue,
        effectiveFrom: editDate,
      });

      queryClient.invalidateQueries({ queryKey: ["salary-config"] });
      if (showHistory) refetchHistory();
      setEditRule(null);

      if (result.affectedDraftRuns.length > 0) {
        setAffectedRuns(result.affectedDraftRuns);
        setShowAffectedDialog(true);
      } else {
        toast({ title: t("payroll.taxSettings.toast.ruleUpdated"), description: t("payroll.taxSettings.toast.ruleUpdatedDesc", { date: editDate }) });
      }
    } catch (err: any) {
      toast({ title: t("payroll.taxSettings.toast.error"), description: err.message || t("payroll.taxSettings.toast.saveRuleError"), variant: "destructive" });
    } finally {
      setSavingRule(false);
    }
  };

  const handleToggleHistory = () => {
    if (!showHistory) refetchHistory();
    setShowHistory((v) => !v);
  };

  if (isLoading) {
    return <div className="py-12 text-center text-muted-foreground text-sm">{t("payroll.taxSettings.loading")}</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-primary" />
          {t("payroll.taxSettings.title")}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t("payroll.taxSettings.subtitle")}
        </p>
      </div>

      {/* Country selector */}
      <Card className="shadow-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Globe className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{config?.countryName ?? t("payroll.taxSettings.defaultCountry")}</p>
                <p className="text-xs text-muted-foreground">{t("payroll.taxSettings.currency")}: {config?.currency ?? "MKD"}</p>
              </div>
            </div>
            {changingCountry ? (
              <div className="flex items-center gap-2">
                <select
                  className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                  defaultValue={config?.countryCode ?? "mk"}
                  onChange={(e) => saveCountryMutation.mutate(e.target.value)}
                  disabled={saveCountryMutation.isPending}
                >
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name} ({c.currency})</option>
                  ))}
                </select>
                <Button size="sm" variant="ghost" onClick={() => setChangingCountry(false)}>{t("common.cancel")}</Button>
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setChangingCountry(true)}>
                {t("payroll.taxSettings.changeCountry")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Calculation Parameters */}
      {config && (config.averageSalary > 0 || config.maxBaseMultiplier > 0) && (
        <Card className="shadow-card">
          <CardHeader className="pb-2 pt-4 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {t("payroll.taxSettings.calculationParams")}
              </CardTitle>
              {!editingParams ? (
                <Button size="sm" variant="ghost" onClick={() => {
                  setParamForm({
                    averageSalary: String(config.averageSalary),
                    maxBaseMultiplier: String(config.maxBaseMultiplier),
                    minNet: String(config.minNet),
                    minGross: String(config.minGross),
                  });
                  setEditingParams(true);
                }}>
                  <Edit3 className="h-3.5 w-3.5 mr-1" /> {t("common.edit")}
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditingParams(false)}>{t("common.cancel")}</Button>
                  <Button size="sm" disabled={saveParamsMutation.isPending} onClick={() => {
                    saveParamsMutation.mutate({
                      averageSalary: parseFloat(paramForm.averageSalary) || undefined,
                      maxBaseMultiplier: parseFloat(paramForm.maxBaseMultiplier) || undefined,
                      minNet: parseFloat(paramForm.minNet) || undefined,
                      minGross: parseFloat(paramForm.minGross) || undefined,
                    });
                  }}>{t("common.save")}</Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-4">
            {editingParams ? (
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "averageSalary", label: t("payroll.taxSettings.averageSalary") },
                  { key: "maxBaseMultiplier", label: t("payroll.taxSettings.maxBaseMultiplier") },
                  { key: "minNet", label: t("payroll.taxSettings.minNet") },
                  { key: "minGross", label: t("payroll.taxSettings.minGross") },
                ].map(({ key, label }) => (
                  <div key={key}>
                    <label className="text-xs text-muted-foreground mb-1 block">{label}</label>
                    <Input
                      type="number"
                      value={(paramForm as any)[key]}
                      onChange={(e) => setParamForm((p) => ({ ...p, [key]: e.target.value }))}
                      className="h-9"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">{t("payroll.taxSettings.averageSalary")}</span><span className="font-medium tabular-nums">{config.averageSalary.toLocaleString()} {config.currency}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("payroll.taxSettings.maxBaseMultiplier")}</span><span className="font-medium tabular-nums">×{config.maxBaseMultiplier} = {(config.averageSalary * config.maxBaseMultiplier).toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("payroll.taxSettings.minNet")}</span><span className="font-medium tabular-nums">{config.minNet.toLocaleString()} {config.currency}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">{t("payroll.taxSettings.minGross")}</span><span className="font-medium tabular-nums">{config.minGross.toLocaleString()} {config.currency}</span></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Deduction Rates */}
      <Card className="shadow-card">
        <CardHeader className="pb-2 pt-4 px-5">
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t("payroll.taxSettings.deductionRates")}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-4">
          <div className="space-y-1">
            {rules.map((rule) => (
              <div key={rule.ruleKey} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/40 group">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{rule.label}</p>
                  <p className="text-xs text-muted-foreground">{t("payroll.taxSettings.effectiveFrom", { date: rule.effectiveFrom })}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold tabular-nums text-primary">
                    {rule.valueType === "rate"
                      ? `${(rule.value * 100).toFixed(2).replace(/\.?0+$/, "")}%`
                      : `${rule.value.toLocaleString()} ${config?.currency ?? ""}`}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0"
                    onClick={() => handleOpenEdit(rule)}
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Change History toggle */}
      <button
        onClick={handleToggleHistory}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <History className="h-4 w-4" />
        {t("payroll.taxSettings.changeHistory")}
        {showHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {showHistory && (
        <Card className="shadow-card">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">{t("payroll.taxSettings.historyColumns.rule")}</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">{t("payroll.taxSettings.historyColumns.value")}</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">{t("payroll.taxSettings.historyColumns.effectiveFrom")}</th>
                    <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">{t("payroll.taxSettings.historyColumns.changedBy")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.length === 0 ? (
                    <tr><td colSpan={4} className="px-4 py-6 text-center text-muted-foreground text-xs">{t("payroll.taxSettings.noHistory")}</td></tr>
                  ) : history.map((h) => (
                    <tr key={h.id} className="border-b last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-2.5">
                        <p className="font-medium">{h.label}</p>
                        <p className="text-xs text-muted-foreground">{h.ruleKey}</p>
                      </td>
                      <td className="px-4 py-2.5 tabular-nums font-medium">
                        {h.valueType === "rate"
                          ? `${(h.value * 100).toFixed(4).replace(/\.?0+$/, "")}%`
                          : h.value.toLocaleString()}
                      </td>
                      <td className="px-4 py-2.5">{h.effectiveFrom}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{h.createdByName ?? t("payroll.taxSettings.systemUser")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Rule Dialog */}
      <Dialog open={!!editRule} onOpenChange={(o) => !o && setEditRule(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("payroll.taxSettings.editRuleTitle", { label: editRule?.label })}</DialogTitle>
            <DialogDescription>
              {t("payroll.taxSettings.editRuleDesc")}
            </DialogDescription>
          </DialogHeader>
          {editRule && (
            <div className="py-2">
              <div className="p-3 rounded-lg bg-muted/50 flex justify-between text-sm mb-2">
                <span className="text-muted-foreground">{t("payroll.taxSettings.currentValue")}</span>
                <span className="font-semibold">
                  {editRule.valueType === "rate"
                    ? `${(editRule.value * 100).toFixed(4).replace(/\.?0+$/, "")}%`
                    : `${editRule.value.toLocaleString()} ${config?.currency ?? ""}`}
                </span>
              </div>
              <FloatField
                label={`${t("payroll.taxSettings.newValue")} ${editRule.valueType === "rate" ? "(%)" : `(${config?.currency ?? ""})`}`}
                required
                type="number"
                value={editValue}
                onChange={(v) => setEditValue(v as string)}
                placeholder={editRule.valueType === "rate" ? t("payroll.taxSettings.ratePlaceholder") : t("payroll.taxSettings.amountPlaceholder")}
              />
              <FloatField
                label={t("payroll.taxSettings.effectiveFromLabel")}
                required
                type="date"
                value={editDate}
                onChange={(v) => setEditDate(v as string)}
              />
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setEditRule(null)}>{t("common.cancel")}</Button>
                <Button
                  disabled={!editValue || !editDate || savingRule}
                  onClick={handleSaveRule}
                >
                  {t("payroll.taxSettings.saveChange")}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Affected Draft Runs Dialog */}
      <AlertDialog open={showAffectedDialog} onOpenChange={setShowAffectedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("payroll.taxSettings.applyToDraftTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("payroll.taxSettings.applyToDraftDesc", { count: affectedRuns.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1 my-2">
            {affectedRuns.map((r) => (
              <div key={r.id} className="flex items-center gap-2 text-sm p-2 rounded bg-muted">
                <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{monthNames[r.month - 1]} {r.year}</span>
                <span className="text-xs text-muted-foreground ml-auto">{t("payroll.taxSettings.draft")}</span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowAffectedDialog(false);
              toast({ title: t("payroll.taxSettings.toast.ruleSaved"), description: t("payroll.taxSettings.toast.ruleSavedDesc") });
            }}>
              {t("payroll.taxSettings.skip")}
            </AlertDialogCancel>
            <AlertDialogAction onClick={async () => {
              setShowAffectedDialog(false);
              for (const run of affectedRuns) {
                await recalcMutation.mutateAsync(run.id);
              }
              toast({ title: t("payroll.taxSettings.toast.recalcDone"), description: t("payroll.taxSettings.toast.recalcDoneDesc", { count: affectedRuns.length }) });
            }}>
              {t("payroll.taxSettings.yesRecalculate")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export { TaxSettingsTab };
